import React, { useCallback, useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faInfoCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Snackbar as MuiSnackbar,
} from "@mui/material";

const icons = {
  success: faCheckCircle,
  error: faExclamationCircle,
  warning: faInfoCircle,
};

const SnackbarContext = React.createContext();
/**
 * @typedef Message
 * @property {string} content
 * @property {string} title
 * @property {'success' | 'error' | 'warning'} severity
 * @property {boolean} autoHideDisabled
 */

export function SnackbarProvider({ children }) {
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(false);

  return (
    <SnackbarContext.Provider value={{ setMessage, message, open, setOpen }}>
      {children}
    </SnackbarContext.Provider>
  );
}

export function useSnackbarMessage() {
  const { setMessage, setOpen } = useContext(SnackbarContext);

  return useCallback(
    ({
      title,
      content,
      autoHideDisabled = false,
      severity = "success",
      anchorOrigin = null,
    }) => {
      setMessage({ title, content, autoHideDisabled, severity, anchorOrigin });
      setOpen(true);
    },
    [setMessage, setOpen],
  );
}

/**
 * A snackbar component that displays a message to the user. Built on top of MUI's Snackbar.
 *
 * @param {SnackbarProps} props
 * @returns {JSX.Element}
 */
export function SnackbarMessages() {
  const { message, open, setOpen } = useContext(SnackbarContext);

  if (!message) return null;
  return (
    <MuiSnackbar
      open={open}
      onClose={() => setOpen(false)}
      autoHideDuration={message.autoHideDisabled ? null : 5000}
      anchorOrigin={
        message.anchorOrigin ?? { vertical: "top", horizontal: "right" }
      }
    >
      <Alert
        onClose={() => setOpen(false)}
        severity={message.severity}
        sx={{ py: 1, pl: 4 }}
        icon={<FontAwesomeIcon widthAuto icon={icons[message.severity]} />}
      >
        <Box
          sx={{
            backgroundColor: "currentColor",
            width: "0.8rem",
            height: "calc(100% - 1rem)",
            borderRadius: "0 1rem 1rem 0",
            position: "absolute",
            left: 0,
            top: "0.5rem",
          }}
        />

        <AlertTitle sx={{ fontWeight: "bold", fontSize: "1.5rem", mb: 0 }}>
          {message.title}
        </AlertTitle>
        <Typography variant="body2" color="textPrimary">
          {message.content}
        </Typography>
      </Alert>
    </MuiSnackbar>
  );
}
